import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Divider,
  Empty,
  Flex,
  Form,
  Input,
  InputNumber,
  List,
  notification,
  Row,
  Segmented,
  Select,
  Space,
  Spin,
  Table,
  Tabs,
  Tag,
  Typography,
} from "antd";
import {
  CarOutlined,
  CopyOutlined,
  CreditCardOutlined,
  SafetyCertificateOutlined,
  SearchOutlined,
  UserOutlined,
  WalletOutlined,
} from "@ant-design/icons";
import {
  useAssignParkingRenterMutation,
  useGetParkingTopupLiqpayFormMutation,
  useGetParkingAccountQuery,
  usePayParkingBillsFromBalanceMutation,
  useUpdateParkingAccessDataMutation,
  useUpdateParkingAccountMutation,
} from "../../services/auth/authService";

const { Text, Title } = Typography;

const ParkingSubComponent = () => {
  const [api, contextHolder] = notification.useNotification();
  const highlightOwnedSpacesTimeoutRef = useRef(null);
  const ownedSpacesSelectRef = useRef(null);
  const { data, isFetching, refetch } = useGetParkingAccountQuery();
  const [activeTab, setActiveTab] = useState("bills");
  const [billStatusFilter, setBillStatusFilter] = useState("unpaid");
  const [selectedOwnedSpaces, setSelectedOwnedSpaces] = useState([]);
  const [shouldHighlightOwnedSpaces, setShouldHighlightOwnedSpaces] = useState(false);
  const [ownedSpaceSearch, setOwnedSpaceSearch] = useState("");
  const [selectedBillIds, setSelectedBillIds] = useState([]);
  const [topUpAmount, setTopUpAmount] = useState(100);
  const [renterPhones, setRenterPhones] = useState({});
  const [carNumbers, setCarNumbers] = useState([]);
  const [parkingRemoteNumber, setParkingRemoteNumber] = useState("");
  const [parkingCardNumber, setParkingCardNumber] = useState("");
  const [updateParkingAccount, { isLoading: isSavingSpaces }] = useUpdateParkingAccountMutation();
  const [updateParkingAccessData, { isLoading: isSavingAccessData }] = useUpdateParkingAccessDataMutation();
  const [assignParkingRenter, { isLoading: isAssigningRenter }] = useAssignParkingRenterMutation();
  const [payParkingBillsFromBalance, { isLoading: isPayingParkingBills }] =
    usePayParkingBillsFromBalanceMutation();
  const [getParkingTopupLiqpayForm, { isLoading: isLiqpayLoading, data: liqpayForm }] =
    useGetParkingTopupLiqpayFormMutation();

  const memberships = data?.memberships || [];
  const bills = data?.bills || [];
  const transactions = data?.transactions || [];
  const managedSpaces = data?.managed_spaces || [];
  const ownerMemberships = memberships.filter((membership) => membership.role === "owner");
  const renterMemberships = memberships.filter((membership) => membership.role === "renter");
  const pendingRequests = data?.pending_requests || [];
  const unpaidBills = bills.filter((bill) => !bill.paid);
  const today = new Date();
  const todayLocalIso = [
    today.getFullYear(),
    String(today.getMonth() + 1).padStart(2, "0"),
    String(today.getDate()).padStart(2, "0"),
  ].join("-");
  const hasOverdueParkingBills = unpaidBills.some((bill) => bill.due_date < todayLocalIso);
  const parkingBlockedDescription = hasOverdueParkingBills
    ? "Є прострочені неоплачені рахунки. Оплатіть їх, щоб відновити доступ до паркінгу."
    : memberships.length === 0 && data?.parking_setup_required === false
      ? "У вас немає активних паркомісць. Дочекайтесь підтвердження заявки. За потреби зверніться до адміністратора."
      : "Паркінг акаунт заблоковано. Зверніться до адміністратора.";
  const filteredBills = bills.filter((bill) => {
    if (billStatusFilter === "paid") {
      return bill.paid;
    }
    if (billStatusFilter === "unpaid") {
      return !bill.paid;
    }
    return true;
  });
  const unpaidBillsTotal = unpaidBills.reduce((sum, bill) => sum + Number(bill.price), 0);
  const selectedBillsTotal = bills
    .filter((bill) => selectedBillIds.includes(bill.id))
    .reduce((sum, bill) => sum + Number(bill.price), 0);
  const parkingBalance = Number(data?.parking_balance || 0);
  const baselineOwnedSpaceIds =
    pendingRequests.length > 0
      ? pendingRequests[0].selected_space_ids || []
      : ownerMemberships.map((membership) => membership.space_id);
  const occupiedOwnerSpaceIds = new Set(data?.occupied_owner_space_ids || []);
  const visibleOwnedSpaceIds = new Set(baselineOwnedSpaceIds);
  const catalogOptions = Object.values(data?.catalog || {})
    .flat()
    .filter((space) => !occupiedOwnerSpaceIds.has(space.id) || visibleOwnedSpaceIds.has(space.id))
    .map((space) => ({
      label: space.label,
      value: space.id,
      level: String(space.level),
      number: String(space.number),
      searchText: `${space.label} ${space.level} ${space.number}`.toLowerCase(),
    }));
  const normalizedOwnedSpaceSearch = ownedSpaceSearch.trim().toLowerCase();
  const filteredCatalogOptions = normalizedOwnedSpaceSearch
    ? catalogOptions.filter((option) =>
        option.number.includes(normalizedOwnedSpaceSearch) ||
        option.searchText.includes(normalizedOwnedSpaceSearch)
      )
    : catalogOptions;
  const normalizeIds = (ids) => [...(ids || [])].map(Number).sort((a, b) => a - b);
  const normalizeStrings = (values) => [...(values || [])].map((value) => value.trim().toUpperCase()).filter(Boolean).sort();
  const baselineCarNumbers = normalizeStrings(data?.car_numbers || []);
  const currentCarNumbers = normalizeStrings(carNumbers);
  const baselineRemoteNumber = (data?.parking_remote_number || "").trim().toUpperCase();
  const baselineCardNumber = (data?.parking_card_number || "").trim().toUpperCase();
  const currentRemoteNumber = parkingRemoteNumber.trim().toUpperCase();
  const currentCardNumber = parkingCardNumber.trim().toUpperCase();
  const hasOwnedSpacesChanges =
    JSON.stringify(normalizeIds(selectedOwnedSpaces)) !== JSON.stringify(normalizeIds(baselineOwnedSpaceIds));
  const hasParkingProfileChanges =
    JSON.stringify(currentCarNumbers) !== JSON.stringify(baselineCarNumbers) ||
    currentRemoteNumber !== baselineRemoteNumber ||
    currentCardNumber !== baselineCardNumber;
  const hasInvalidRemoteNumber = currentRemoteNumber && !/^PR\d{9}$/.test(currentRemoteNumber);
  const hasInvalidCardNumber = currentCardNumber && !/^CR\d{9}$/.test(currentCardNumber);
  const isOwnedSpaceSearchActive = normalizedOwnedSpaceSearch.length > 0;

  useEffect(() => {
    if (!data) {
      return;
    }
    const nextPendingRequests = data.pending_requests || [];
    const nextOwnerMemberships = (data.memberships || []).filter(
      (membership) => membership.role === "owner"
    );
    if (nextPendingRequests.length > 0) {
      setSelectedOwnedSpaces(nextPendingRequests[0].selected_space_ids || []);
    } else {
      setSelectedOwnedSpaces(nextOwnerMemberships.map((membership) => membership.space_id));
    }
    setCarNumbers(data.car_numbers || []);
    setParkingRemoteNumber(data.parking_remote_number || "");
    setParkingCardNumber(data.parking_card_number || "");
  }, [data]);

  useEffect(() => {
    if (topUpAmount > 0) {
      getParkingTopupLiqpayForm(topUpAmount);
    }
  }, [topUpAmount, getParkingTopupLiqpayForm]);

  useEffect(() => () => {
    if (highlightOwnedSpacesTimeoutRef.current) {
      clearTimeout(highlightOwnedSpacesTimeoutRef.current);
    }
  }, []);

  const notifySuccess = (description) => {
    api.success({ message: "Повідомлення", description });
  };

  const notifyError = (description) => {
    api.error({ message: "Повідомлення", description });
  };

  const notifyInfo = (key, description) => {
    api.info({ key, message: "Повідомлення", description });
  };

  const getErrorMessage = (rejected, fallback) => {
    const responseData = rejected?.data;
    if (typeof responseData === "string" && responseData.replaceAll('"', "") === "Insufficient funds") {
      return "Недостатньо коштів на паркінг-балансі";
    }
    return typeof responseData === "string"
      ? responseData
      : responseData?.error || Object.values(responseData || {}).join("") || fallback;
  };

  const onOwnedSpacesChange = (spaceIds) => {
    setSelectedOwnedSpaces(spaceIds);
    const changed =
      JSON.stringify(normalizeIds(spaceIds)) !== JSON.stringify(normalizeIds(baselineOwnedSpaceIds));
    notifyInfo(
      "parking-spaces-selection",
      changed
        ? "Вибір паркомісць змінено. Надішліть зміни на підтвердження адміністратору."
        : "Вибір паркомісць повернуто до поточного стану."
    );
  };

  const onBillSelectionChange = (keys) => {
    setSelectedBillIds(keys);
    if (keys.length === 0) {
      notifyInfo("parking-bills-selection", "Вибір рахунків очищено");
      return;
    }
    const total = bills
      .filter((bill) => keys.includes(bill.id))
      .reduce((sum, bill) => sum + Number(bill.price), 0);
    notifyInfo(
      "parking-bills-selection",
      `Обрано рахунків: ${keys.length}. До списання з балансу: ${total} грн.`
    );
  };

  const onBillStatusFilterChange = (statusFilter) => {
    setBillStatusFilter(statusFilter);
    setSelectedBillIds([]);
  };

  const onChooseOwnedSpacesFromAlert = () => {
    setActiveTab("spaces");
    setShouldHighlightOwnedSpaces(true);
    setTimeout(() => {
      ownedSpacesSelectRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 0);
    if (highlightOwnedSpacesTimeoutRef.current) {
      clearTimeout(highlightOwnedSpacesTimeoutRef.current);
    }
    highlightOwnedSpacesTimeoutRef.current = setTimeout(() => {
      setShouldHighlightOwnedSpaces(false);
      highlightOwnedSpacesTimeoutRef.current = null;
    }, 2400);
  };

  const onSaveSpaces = () => {
    updateParkingAccount({ selected_space_ids: selectedOwnedSpaces })
      .unwrap()
      .then(() => {
        notifySuccess("Зміни паркомісць відправлено на підтвердження адміністратору");
        refetch();
      })
      .catch((rejected) => {
        notifyError(getErrorMessage(rejected, "Не вдалося відправити зміни паркомісць"));
      });
  };

  const onSaveAccessData = () => {
    if (hasInvalidRemoteNumber) {
      notifyError("Пульт має мати формат PR + 9 цифр");
      return;
    }
    if (hasInvalidCardNumber) {
      notifyError("Картка має мати формат CR + 9 цифр");
      return;
    }
    updateParkingAccessData({
      car_numbers: currentCarNumbers,
      parking_remote_number: currentRemoteNumber,
      parking_card_number: currentCardNumber,
    })
      .unwrap()
      .then(() => {
        notifySuccess("Дані доступу паркінгу збережено");
        refetch();
      })
      .catch((rejected) => {
        notifyError(getErrorMessage(rejected, "Не вдалося зберегти дані доступу"));
      });
  };

  const onAssignRenter = (event, spaceId) => {
    event.preventDefault();
    event.stopPropagation();
    const phone = (renterPhones[spaceId] || "").trim();
    if (!phone) {
      notifyError("Вкажіть номер телефону орендаря");
      return;
    }
    if (!/^\+380\d{9}$/.test(phone)) {
      notifyError("Номер орендаря має мати формат +380XXXXXXXXX");
      return;
    }
    assignParkingRenter({ space_id: spaceId, phone })
      .unwrap()
      .then(() => {
        notifySuccess("Орендаря збережено");
        setRenterPhones((previous) => ({ ...previous, [spaceId]: "" }));
        refetch();
      })
      .catch((rejected) => {
        notifyError(getErrorMessage(rejected, "Не вдалося призначити орендаря"));
      });
  };

  const getRenterInviteText = (managedSpace) => (
    `Вас запрошено до реєстрування в кабінеті https://getman.biz/register для доступу до паркомісця: рівень ${managedSpace.level}, місце ${managedSpace.number}.`
  );

  const onCopyRenterInvite = (event, managedSpace) => {
    event.preventDefault();
    event.stopPropagation();
    const inviteText = getRenterInviteText(managedSpace);

    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(inviteText)
        .then(() => {
          notifySuccess("Текст запрошення скопійовано");
        })
        .catch(() => {
          notifyError("Не вдалося скопіювати текст запрошення");
        });
      return;
    }

    const textArea = document.createElement("textarea");
    textArea.value = inviteText;
    textArea.setAttribute("readonly", "");
    textArea.style.position = "fixed";
    textArea.style.opacity = "0";
    document.body.appendChild(textArea);
    textArea.select();
    const copied = document.execCommand("copy");
    document.body.removeChild(textArea);
    copied
      ? notifySuccess("Текст запрошення скопійовано")
      : notifyError("Не вдалося скопіювати текст запрошення");
  };

  const onPayParkingBills = () => {
    if (selectedBillIds.length === 0) {
      notifyError("Оберіть рахунки для оплати");
      return;
    }
    payParkingBillsFromBalance(selectedBillIds)
      .unwrap()
      .then(() => {
        notifySuccess("Оплату списано з балансу");
        setSelectedBillIds([]);
        refetch();
      })
      .catch((rejected) => {
        const errorMessage = getErrorMessage(rejected, "Не вдалося оплатити рахунки");
        if (errorMessage === "Недостатньо коштів на паркінг-балансі") {
          const requiredTopUpAmount = Math.max(1, Math.ceil(selectedBillsTotal - parkingBalance));
          setTopUpAmount(requiredTopUpAmount);
          setActiveTab("topup");
          notifyInfo(
            "parking-topup-required",
            `Недостатньо коштів. Поповніть паркінг-баланс на ${requiredTopUpAmount} грн.`
          );
          return;
        }
        notifyError(errorMessage);
      });
  };

  const billColumns = [
    {
      title: "Паркомісце",
      key: "space",
      render: (_, record) => (
        <div className="parking-bill-space">
          <span className="parking-bill-number">{record.number}</span>
          <span className="parking-bill-level">{`Рівень ${record.level}`}</span>
        </div>
      ),
    },
    {
      title: "Місяць",
      dataIndex: "billing_month",
      key: "billing_month",
    },
    {
      title: "Сума",
      dataIndex: "price",
      key: "price",
    },
    {
      title: "Стан",
      dataIndex: "paid",
      key: "paid",
      render: (paid) => paid ? "Оплачено" : "Не оплачено",
    },
  ];

  const transactionColumns = [
    {
      title: "Паркомісце",
      key: "space",
      render: (_, record) => (
        <div className="parking-bill-space">
          <span className="parking-bill-number">{record.number}</span>
          <span className="parking-bill-level">{`Рівень ${record.level}`}</span>
        </div>
      ),
    },
    {
      title: "Сума",
      dataIndex: "amount",
      key: "amount",
    },
    {
      title: "Дата",
      dataIndex: "date",
      key: "date",
      render: (date) => date ? new Date(date).toLocaleString("uk-UA") : "-",
    },
    {
      title: "Хто оплатив",
      key: "paid_by",
      render: (_, record) => {
        if (record.paid_by_name && record.paid_by_phone) {
          return `${record.paid_by_name} (${record.paid_by_phone})`;
        }
        return record.paid_by_name || record.paid_by_phone || "-";
      },
    },
  ];

  const topUpPresetAmounts = [100, 200, 500];
  const parkingTabOptions = [
    { key: "bills", label: `Рахунки (${unpaidBills.length})` },
    { key: "topup", label: "Поповнення" },
    { key: "spaces", label: "Мої місця" },
    { key: "renters", label: "Орендарі" },
    { key: "history", label: "Історія" },
  ];

  const renderKpiCard = ({ tabKey, icon, value, label }) => {
    const openTab = () => setActiveTab(tabKey);

    return (
      <Card
        className="parking-kpi-card"
        bordered={false}
        hoverable
        role="button"
        tabIndex={0}
        onClick={openTab}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            openTab();
          }
        }}
      >
        {icon}
        <div className="parking-kpi-value">{value}</div>
        <div className="parking-kpi-label">{label}</div>
      </Card>
    );
  };

  if (isFetching) {
    return (
      <div className="parking-shell">
        {contextHolder}
        <Spin />
      </div>
    );
  }

  return (
    <div className="parking-shell">
      {contextHolder}
      <Card className="parking-hero parking-overview-card" bordered={false}>
        <Flex className="parking-hero__top" justify="space-between" gap={16} wrap="wrap">
          <div>
            <Text className="parking-eyebrow">Parking Account</Text>
            <Title level={3} className="parking-title">
              Паркінг
            </Title>
            <Text className="parking-subtitle">
              Короткий стан акаунту, швидка оплата та окремі розділи для керування.
            </Text>
          </div>
          <div className="parking-hero__price">
            <Text className="parking-hero__price-label">Місячний тариф</Text>
            <div className="parking-hero__price-value">{data?.monthly_price} грн</div>
          </div>
        </Flex>
        <Row gutter={[12, 12]} className="parking-kpis">
          <Col xs={12} md={6}>
            {renderKpiCard({
              tabKey: "spaces",
              icon: <CarOutlined className="parking-kpi-icon" />,
              value: ownerMemberships.length,
              label: "Мої місця",
            })}
          </Col>
          <Col xs={12} md={6}>
            {renderKpiCard({
              tabKey: "spaces",
              icon: <UserOutlined className="parking-kpi-icon" />,
              value: renterMemberships.length,
              label: "Я орендую",
            })}
          </Col>
          <Col xs={12} md={6}>
            {renderKpiCard({
              tabKey: "bills",
              icon: <CreditCardOutlined className="parking-kpi-icon" />,
              value: unpaidBills.length,
              label: "Неоплачені",
            })}
          </Col>
          <Col xs={12} md={6}>
            {renderKpiCard({
              tabKey: "bills",
              icon: <SafetyCertificateOutlined className="parking-kpi-icon" />,
              value: `${unpaidBillsTotal} грн`,
              label: "Борг",
            })}
          </Col>
          <Col xs={12} md={6}>
            {renderKpiCard({
              tabKey: "topup",
              icon: <WalletOutlined className="parking-kpi-icon" />,
              value: `${data?.parking_balance ?? 0} грн`,
              label: "Баланс",
            })}
          </Col>
        </Row>
      </Card>

      <Space direction="vertical" size={16} style={{ width: "100%" }}>
        {unpaidBills.length > 0 && (
          <Card className="parking-priority-card" bordered={false}>
            <Flex justify="space-between" align="center" gap={16} wrap="wrap">
              <div>
                <Text className="parking-payment-strip__label">Потребує уваги</Text>
                <div className="parking-payment-strip__value">{unpaidBillsTotal} грн</div>
                <Text className="parking-hint">
                  Неоплачених рахунків: {unpaidBills.length}. Виберіть потрібні рахунки у вкладці нижче.
                </Text>
              </div>
              <Button
                htmlType="button"
                shape="round"
                type="primary"
                size="large"
                className="parking-pay-button"
                onClick={() => setActiveTab("bills")}
              >
                Перейти до рахунків
              </Button>
            </Flex>
          </Card>
        )}

        {data?.parking_setup_required && (
          <Alert
            type="warning"
            showIcon
            message="Спочатку оберіть ваші паркомісця"
            description={
              <Space size={8} wrap>
                <span>Без цього паркінг акаунт не буде активовано.</span>
                <Button htmlType="button" size="small" onClick={onChooseOwnedSpacesFromAlert}>
                  Обрати
                </Button>
              </Space>
            }
          />
        )}
        {data?.parking_blocked && (
          <Alert
            type="error"
            showIcon
            message="Паркінг акаунт заблоковано"
            description={parkingBlockedDescription}
          />
        )}

        <div className="parking-workspace-tabs">
          <div className="parking-workspace-tabbar" role="tablist" aria-label="Розділи паркінгу">
            {parkingTabOptions.map((tab) => (
              <button
                key={tab.key}
                type="button"
                className={`parking-workspace-tabbar__item ${
                  activeTab === tab.key ? "parking-workspace-tabbar__item--active" : ""
                }`}
                role="tab"
                aria-selected={activeTab === tab.key}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <Tabs
            className="parking-workspace-tabs__panes"
            activeKey={activeTab}
            onChange={setActiveTab}
            renderTabBar={() => null}
            items={[
            {
              key: "bills",
              label: `Рахунки (${unpaidBills.length})`,
              children: (
                <Card className="parking-section-card" title="Рахунки паркінгу" bordered={false}>
                  <Flex className="parking-bills-toolbar" justify="space-between" align="center" gap={12} wrap="wrap">
                    <Text className="parking-hint">
                      Неоплачені: {unpaidBills.length} / Оплачені: {bills.length - unpaidBills.length}
                    </Text>
                    <Segmented
                      value={billStatusFilter}
                      onChange={onBillStatusFilterChange}
                      options={[
                        { label: "Всі", value: "all" },
                        { label: "Неоплачені", value: "unpaid" },
                        { label: "Оплачені", value: "paid" },
                      ]}
                    />
                  </Flex>
                  <Table
                    className="parking-bills-table"
                    columns={billColumns}
                    dataSource={filteredBills}
                    rowKey="id"
                    pagination={false}
                    scroll={{ x: 720, y: 420 }}
                    rowClassName={(record) => record.paid ? "parking-bill-row-paid" : "parking-bill-row-unpaid"}
                    rowSelection={{
                      type: "checkbox",
                      selectedRowKeys: selectedBillIds,
                      onChange: onBillSelectionChange,
                      getCheckboxProps: (record) => ({ disabled: record.paid }),
                    }}
                    locale={{ emptyText: "Немає рахунків для відображення" }}
                  />
                  <Flex className="parking-payment-strip" justify="space-between" align="center" gap={12} wrap="wrap">
                    <div>
                      <Text className="parking-payment-strip__label">До оплати</Text>
                      <div className="parking-payment-strip__value">{selectedBillsTotal} грн</div>
                    </div>
                    {selectedBillIds.length > 0 && (
                      <Flex align="center" gap={12} wrap="wrap">
                        <Button
                          htmlType="button"
                          shape="round"
                          type="primary"
                          size="large"
                          className="parking-pay-button"
                          loading={isPayingParkingBills}
                          onClick={onPayParkingBills}
                        >
                          Оплатити вибрані паркомісця
                        </Button>
                      </Flex>
                    )}
                  </Flex>
                </Card>
              ),
            },
            {
              key: "topup",
              label: "Поповнення",
              children: (
                <Row gutter={[16, 16]}>
                  <Col xs={24} lg={10}>
                    <Card className="compact-section-card compact-payment-card parking-topup-card" bordered={false}>
                      <div className="compact-card-title">Поповнити паркінг-баланс</div>
                      <div className="parking-topup-balance">
                        <Text className="parking-topup-balance__label">Поточний паркінг-баланс</Text>
                        <div className="parking-topup-balance__value">
                          {`${data?.parking_balance ?? 0} грн`}
                        </div>
                      </div>
                      <Flex className="parking-topup-presets" gap={8} wrap="wrap">
                        {topUpPresetAmounts.map((preset) => (
                          <Button
                            key={preset}
                            size="small"
                            type={topUpAmount === preset ? "primary" : "default"}
                            onClick={() => setTopUpAmount(preset)}
                          >
                            {preset} грн
                          </Button>
                        ))}
                      </Flex>
                      <Form layout="vertical" style={{ marginTop: 16 }}>
                        <Form.Item label="Сума">
                          <InputNumber
                            type="number"
                            min={1}
                            value={topUpAmount}
                            onChange={(value) => setTopUpAmount(value || 0)}
                            addonAfter="₴"
                            controls={false}
                            style={{ width: "100%" }}
                          />
                        </Form.Item>
                      </Form>
                      <Text className="parking-hint">
                        Поповнення відбувається через LiqPay. Після оплати паркінг-баланс оновиться автоматично.
                      </Text>
                    </Card>
                  </Col>
                  <Col xs={24} lg={14}>
                    <Card className="compact-section-card compact-payment-card parking-topup-card" bordered={false}>
                      {isLiqpayLoading ? (
                        <Spin />
                      ) : (
                        <div
                          className="replenishment-payment-widget"
                          dangerouslySetInnerHTML={{ __html: liqpayForm || "" }}
                        />
                      )}
                    </Card>
                  </Col>
                </Row>
              ),
            },
            {
              key: "spaces",
              label: "Мої місця",
              children: (
                <Row gutter={[16, 16]}>
                  <Col xs={24} lg={14}>
                    <Card className="parking-section-card" title="Керування паркомісцями" bordered={false}>
                      <Form layout="vertical">
                        <div ref={ownedSpacesSelectRef}>
                          <Form.Item
                            label={<span className="parking-section-label">Обрані паркомісця власника</span>}
                            extra={
                              isOwnedSpaceSearchActive
                                ? `Знайдено варіантів: ${filteredCatalogOptions.length}. Зміни набувають чинності після підтвердження адміністратором.`
                                : "Зміни паркомісць набувають чинності після підтвердження адміністратором."
                            }
                          >
                            <Input
                              allowClear
                              className="parking-space-search"
                              prefix={<SearchOutlined />}
                              inputMode="numeric"
                              placeholder="Пошук за номером місця, наприклад 123"
                              value={ownedSpaceSearch}
                              onChange={(event) => setOwnedSpaceSearch(event.target.value)}
                            />
                            <Select
                              mode="multiple"
                              value={selectedOwnedSpaces}
                              onChange={onOwnedSpacesChange}
                              options={filteredCatalogOptions}
                              showSearch
                              filterOption={(input, option) =>
                                (option?.searchText || "").includes(input.trim().toLowerCase()) ||
                                (option?.number || "").includes(input.trim().toLowerCase())
                              }
                              size="large"
                              className={`parking-select ${shouldHighlightOwnedSpaces ? "parking-select--highlighted" : ""}`}
                              placeholder="Оберіть паркомісця"
                            />
                          </Form.Item>
                        </div>
                        <Flex justify="flex-end" align="center" gap={12} wrap="wrap">
                          <Button
                            htmlType="button"
                            type="primary"
                            size="large"
                            disabled={!hasOwnedSpacesChanges}
                            loading={isSavingSpaces}
                            onClick={onSaveSpaces}
                          >
                            Надіслати на підтвердження
                          </Button>
                        </Flex>
                        <Divider />
                        <Flex vertical gap={4} style={{ marginBottom: 16 }}>
                          <Text strong>Дані доступу</Text>
                          <Text className="parking-hint">
                            Номери авто, пульт і картка зберігаються одразу, без заявки на зміну паркомісць.
                          </Text>
                        </Flex>
                        <Form.Item
                          label={<span className="parking-section-label">Номери авто</span>}
                          extra="Можна вказати декілька номерів. Натисніть Enter після кожного номера."
                        >
                          <Select
                            mode="tags"
                            value={carNumbers}
                            onChange={(values) => setCarNumbers(values)}
                            tokenSeparators={[",", " "]}
                            placeholder="AA1234BB"
                            size="large"
                            className="parking-select"
                          />
                        </Form.Item>
                        <Row gutter={[12, 0]}>
                          <Col xs={24} md={12}>
                            <Form.Item
                              label={<span className="parking-section-label">Пульт</span>}
                              validateStatus={hasInvalidRemoteNumber ? "error" : ""}
                              help={hasInvalidRemoteNumber ? "Формат: PR + 9 цифр" : "Наприклад: PR123456789"}
                            >
                              <Input
                                value={parkingRemoteNumber}
                                onChange={(event) => setParkingRemoteNumber(event.target.value.toUpperCase())}
                                placeholder="PR123456789"
                                size="large"
                              />
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={12}>
                            <Form.Item
                              label={<span className="parking-section-label">Картка</span>}
                              validateStatus={hasInvalidCardNumber ? "error" : ""}
                              help={hasInvalidCardNumber ? "Формат: CR + 9 цифр" : "Наприклад: CR123456789"}
                            >
                              <Input
                                value={parkingCardNumber}
                                onChange={(event) => setParkingCardNumber(event.target.value.toUpperCase())}
                                placeholder="CR123456789"
                                size="large"
                              />
                            </Form.Item>
                          </Col>
                        </Row>
                        <Flex justify="flex-end" align="center" gap={12} wrap="wrap">
                          <Button
                            htmlType="button"
                            type="default"
                            size="large"
                            disabled={!hasParkingProfileChanges || hasInvalidRemoteNumber || hasInvalidCardNumber}
                            loading={isSavingAccessData}
                            onClick={onSaveAccessData}
                          >
                            Зберегти
                          </Button>
                        </Flex>
                      </Form>
                      {pendingRequests.length > 0 && (
                        <>
                          <Divider />
                          <div className="parking-inline-note">
                            <Text strong>Очікує підтвердження:</Text>{" "}
                            <Text>{pendingRequests[0].spaces.map((space) => space.label).join(", ")}</Text>
                          </div>
                        </>
                      )}
                    </Card>
                  </Col>
                  <Col xs={24} lg={10}>
                    <Card className="parking-section-card" title="Активні ролі" bordered={false}>
                      <div className="parking-role-grid">
                        <div className="parking-role-panel">
                          <Text className="parking-role-title">Власник</Text>
                          {ownerMemberships.length > 0 ? (
                            <List
                              dataSource={ownerMemberships}
                              split={false}
                              renderItem={(membership) => (
                                <List.Item className="parking-role-item">
                                  <span>{`Рівень ${membership.level} / Місце ${membership.number}`}</span>
                                  <Tag color="blue">Власник</Tag>
                                </List.Item>
                              )}
                            />
                          ) : (
                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Немає активних місць" />
                          )}
                        </div>
                        <div className="parking-role-panel">
                          <Text className="parking-role-title">Орендар</Text>
                          {renterMemberships.length > 0 ? (
                            <List
                              dataSource={renterMemberships}
                              split={false}
                              renderItem={(membership) => (
                                <List.Item className="parking-role-item">
                                  <span>{`Рівень ${membership.level} / Місце ${membership.number}`}</span>
                                  <Tag color="green">Орендар</Tag>
                                </List.Item>
                              )}
                            />
                          ) : (
                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Немає орендованих місць" />
                          )}
                        </div>
                      </div>
                    </Card>
                  </Col>
                </Row>
              ),
            },
            {
              key: "renters",
              label: "Орендарі",
              children: (
                <Card className="parking-section-card" title="Орендарі для ваших паркомісць" bordered={false}>
                  {managedSpaces.length > 0 ? (
                    <div className="parking-managed-grid">
                      {managedSpaces.map((managedSpace) => (
                        <Card key={managedSpace.space_id} className="parking-managed-card" bordered={false}>
                          <Flex justify="space-between" align="flex-start" gap={12} wrap="wrap">
                            <div>
                              <Text className="parking-managed-card__title">
                                {`Рівень ${managedSpace.level} / Місце ${managedSpace.number}`}
                              </Text>
                              <div className="parking-managed-card__status">
                                {managedSpace.current_renter ? (
                                  <Text type="secondary">
                                    {`Поточний орендар: ${[managedSpace.current_renter.first_name, managedSpace.current_renter.last_name].filter(Boolean).join(" ") || "Користувач"} (${managedSpace.current_renter.phone})`}
                                  </Text>
                                ) : managedSpace.pending_invite_phone ? (
                                  <Text type="secondary">
                                    {`Очікує реєстрації: ${managedSpace.pending_invite_phone}`}
                                  </Text>
                                ) : (
                                  <Text type="secondary">Орендар ще не призначений</Text>
                                )}
                              </div>
                            </div>
                            <Tag color={managedSpace.current_renter ? "green" : managedSpace.pending_invite_phone ? "gold" : "default"}>
                              {managedSpace.current_renter ? "Активний орендар" : managedSpace.pending_invite_phone ? "Запрошення" : "Вільне місце"}
                            </Tag>
                          </Flex>
                          <Flex className="parking-managed-card__controls" gap={10} wrap="wrap" align="center">
                            <Input
                              className="parking-managed-card__input"
                              value={renterPhones[managedSpace.space_id] || ""}
                              placeholder="+380XXXXXXXXX"
                              status={
                                renterPhones[managedSpace.space_id] &&
                                !/^\+380\d{9}$/.test((renterPhones[managedSpace.space_id] || "").trim())
                                  ? "error"
                                  : ""
                              }
                              onChange={(event) =>
                                setRenterPhones((previous) => ({
                                  ...previous,
                                  [managedSpace.space_id]: event.target.value,
                                }))
                              }
                            />
                            <Button
                              htmlType="button"
                              type="primary"
                              loading={isAssigningRenter}
                              onClick={(event) => onAssignRenter(event, managedSpace.space_id)}
                            >
                              Призначити орендаря
                            </Button>
                            {(managedSpace.pending_invite_phone || /^\+380\d{9}$/.test((renterPhones[managedSpace.space_id] || "").trim())) && (
                              <Button
                                htmlType="button"
                                icon={<CopyOutlined />}
                                onClick={(event) => onCopyRenterInvite(event, managedSpace)}
                              >
                                Скопіювати запрошення
                              </Button>
                            )}
                          </Flex>
                        </Card>
                      ))}
                    </div>
                  ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Немає паркомісць для керування орендарями" />
                  )}
                </Card>
              ),
            },
            {
              key: "history",
              label: "Історія",
              children: (
                <Card className="parking-section-card" title="Історія оплат паркінгу" bordered={false}>
                  <Table
                    className="parking-bills-table"
                    columns={transactionColumns}
                    dataSource={transactions}
                    rowKey="id"
                    pagination={false}
                    scroll={{ x: 720, y: 360 }}
                    locale={{ emptyText: "Немає транзакцій для відображення" }}
                  />
                </Card>
              ),
            },
            ]}
          />
        </div>
      </Space>
    </div>
  );
};

export default ParkingSubComponent;
